import { FlightBadgeKind } from "../../../api/flightAlerts.service";
import { FlightBadgeState, FlightFixture } from "../types";

/**
 * Pure lookup from the backend's already-precedence-resolved `badge` (DASH-6203 Phase 1). No
 * threshold, no schedule-diff recompute — the backend has already decided
 * cancelled > landed/departed > gate states > delayed/early/onTime; this just maps its wire enum,
 * which is where the old midnight-crossing recompute bug used to live.
 */
export type BadgeInput = Pick<FlightFixture, "badge" | "badgeDelayMinutes">;

const BADGE_KIND_TO_STATE: Record<FlightBadgeKind, FlightBadgeState> = {
  [FlightBadgeKind.CANCELLED]: "cancelled",
  [FlightBadgeKind.LANDED]: "landed",
  [FlightBadgeKind.DEPARTED]: "departed",
  [FlightBadgeKind.GATE_CLOSED]: "gateClosed",
  [FlightBadgeKind.FINAL_CALL]: "finalCall",
  [FlightBadgeKind.BOARDING]: "boarding",
  [FlightBadgeKind.DELAYED]: "delayed",
  [FlightBadgeKind.EARLY]: "early",
  [FlightBadgeKind.ON_TIME]: "onTime",
  [FlightBadgeKind.STATUS_UNAVAILABLE]: "statusUnavailable",
};

// Legacy fixtures that predate this field carry no `badge` at all — fail safe to
// "statusUnavailable" rather than falling through to a schedule-comparison guess that could
// fold a no-estimate leg into "on time".
export const resolveBadgeState = (fixture: BadgeInput): FlightBadgeState =>
  fixture.badge === undefined ? "statusUnavailable" : BADGE_KIND_TO_STATE[fixture.badge];

const STATIC_LABELS: Record<Exclude<FlightBadgeState, "delayed" | "early">, string> = {
  onTime: "On time",
  boarding: "Boarding",
  finalCall: "Final call",
  gateClosed: "Gate closed",
  landed: "Landed",
  departed: "Departed",
  cancelled: "Cancelled",
  statusUnavailable: "Status unavailable",
};

/**
 * Formats the badge label straight from the backend's own `badgeDelayMinutes` — never a
 * client-recomputed diff, so a midnight-crossing schedule/estimate pair can't turn into
 * "Early 1420m" the way a local minutes-since-midnight subtraction could.
 */
export const formatBadgeLabel = (fixture: BadgeInput): string => {
  const state = resolveBadgeState(fixture);
  if (state === "delayed" || state === "early") {
    const minutes = Math.abs(fixture.badgeDelayMinutes ?? 0);
    return state === "delayed" ? `Delayed ${minutes} min` : `Early ${minutes} min`;
  }
  return STATIC_LABELS[state];
};

/**
 * Formats `lastUpdated` (an ISO-8601 UTC instant, or null/absent) in HKT. Parses the ISO instant
 * directly — never routed through time.helper.ts's `timeToMinutes`, which expects the backend's
 * "YYYY-MM-DD HH:MM" local-string shape, not a UTC instant. Renders nothing (never "NaN:NaN")
 * when there's no valid instant to show.
 */
export const formatLastUpdatedHKT = (lastUpdated: string | null | undefined): string | null => {
  if (!lastUpdated) return null;
  const parsed = new Date(lastUpdated);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Hong_Kong",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(parsed);
};

export const isResolvedBadge = (badge: FlightBadgeState): boolean =>
  badge === "landed" || badge === "departed" || badge === "cancelled";
